// # Notes Component
import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../../auth/state';
import { useNavigate } from 'react-router-dom';
import './my_notes.css';
import DashboardFooter from '../../components/DashboardFooter';
import DashboardHeader from '../../components/DashboardHeader';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../auth/firebase';
import cacheManager from '../../auth/cache_manager';
import stepOneIcon from '../../aneo_media/mentee/one.png';
import stepTwoIcon from '../../aneo_media/mentee/two.png';

// # Import all icons
import anatomyIcon from '../library/step1/step1_icons/anatomy.png';
import biochemistryIcon from '../library/step1/step1_icons/biochemistry.png';
import bloodIcon from '../library/step1/step1_icons/blood.png';
import cnsIcon from '../library/step1/step1_icons/cns.png';
import earIcon from '../library/step1/step1_icons/ear.png';
import emergencyIcon from '../library/step1/step1_icons/emergency.png';
import endocrineIcon from '../library/step1/step1_icons/endocrine.png';
import eyeIcon from '../library/step1/step1_icons/eye.png';
import gitIcon from '../library/step1/step1_icons/git.png';
import healthcareIcon from '../library/step1/step1_icons/healthcare.png';
import heartIcon from '../library/step1/step1_icons/heart.png';
import immunologyIcon from '../library/step1/step1_icons/immunology.png';
import infectiousIcon from '../library/step1/step1_icons/infectious.png';
import kidneyIcon from '../library/step1/step1_icons/kidney.png';
import lungsIcon from '../library/step1/step1_icons/lungs.png';
import musculoskeletalIcon from '../library/step1/step1_icons/musculoskeletal.png';
import newbornIcon from '../library/step1/step1_icons/newborn.png';
import pathologyIcon from '../library/step1/step1_icons/pathology.png';
import pharmaIcon from '../library/step1/step1_icons/pharma.png';
import psychiatryIcon from '../library/step1/step1_icons/psychiatry.png';
import publicHealthIcon from '../library/step1/step1_icons/public_health.png';
import qualityIcon from '../library/step1/step1_icons/quality.png';
import reproductiveIcon from '../library/step1/step1_icons/reproductive-system.png';
import revisionIcon from '../library/step1/step1_icons/revision.png';
import skinIcon from '../library/step1/step1_icons/skin.png';
import statisticsIcon from '../library/step1/step1_icons/statistics.png';
import surgeryIcon from '../library/step1/step1_icons/surgery.png';
import wellPatientIcon from '../library/step1/step1_icons/well_patient.png';

function MyNotes() {
  const { userProfile, user } = useAuthStore();
  const navigate = useNavigate();
  const [isDarkMode, setIsDarkMode] = useState(false);

  // # Initialize all steps as collapsed
  const [expandedSteps, setExpandedSteps] = useState({
    step1: false,
    step2: false
  });

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  const toggleStep = (stepId) => {
    setExpandedSteps(prev => ({
      ...prev,
      [stepId]: !prev[stepId]
    }));
  };

  // # Format chapter ID for URL consistency with library files
  const formatChapterIdForUrl = (stepId, chapterId) => {
    // # For Step 2 chapters (using uppercase with underscores format)
    if (stepId.includes('Step 2') && /^[A-Z_,&]+$/.test(chapterId)) {
      return chapterId
        .toLowerCase()
        .replace(/,/g, '') // Remove commas first
        .replace(/&/g, 'and')
        .replace(/\s+/g, '_'); // Replace spaces with single underscore
    }
    
    // # For Step 1 chapters (using standard format)
    return chapterId.toLowerCase().replace(/[\s&]+/g, '_');
  };

  // # Handle chapter navigation
  const handleChapterClick = (e, stepId, chapterId) => {
    // Prevent event bubbling
    e.stopPropagation();
    
    // # Get current path to determine if we're in admin mode
    const isAdmin = window.location.pathname.startsWith('/admin');
    const basePath = isAdmin ? '/admin/notes' : '/notes';
    
    // # Format chapter ID for URL consistency with library files
    const formattedChapterId = formatChapterIdForUrl(stepId, chapterId);
    
    // # Log navigation attempt
    console.log('Navigating to:', `${basePath}/${stepId}/${formattedChapterId}`);
    
    // # Navigate to the chapter notes page
    navigate(`${basePath}/${stepId}/${formattedChapterId}`, { replace: true });
  };

  // # Format title for Step 2 display
  const getDisplayTitle = (title) => {
    return title
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  const steps = [
    {
      id: 'USMLE Step 1 -',
      title: 'USMLE Step 1',
      icon: stepOneIcon,
      chapters: [
        { id: 'Biochemistry', title: 'Biochemistry', icon: biochemistryIcon },
        { id: 'Immunology', title: 'Immunology', icon: immunologyIcon },
        { id: 'Microbiology', title: 'Microbiology', icon: infectiousIcon },
        { id: 'General Pathology', title: 'General Pathology', icon: pathologyIcon },
        { id: 'Pharmacology', title: 'Pharmacology', icon: pharmaIcon },
        { id: 'Public Health Sciences', title: 'Public Health Sciences', icon: publicHealthIcon },
        { id: 'Anatomy & Histology', title: 'Anatomy & Histology', icon: anatomyIcon },
        { id: 'Cardiovascular', title: 'Cardiovascular', icon: heartIcon },
        { id: 'Endocrine', title: 'Endocrine', icon: endocrineIcon },
        { id: 'Gastrointestinal', title: 'Gastrointestinal', icon: gitIcon },
        { id: 'Hematology & Oncology', title: 'Hematology & Oncology', icon: bloodIcon },
        { id: 'Musculoskeletal & Connective Tissue', title: 'Musculoskeletal & Connective Tissue', icon: musculoskeletalIcon },
        { id: 'Dermatology', title: 'Dermatology', icon: skinIcon },
        { id: 'Neurology', title: 'Neurology', icon: cnsIcon },
        { id: 'Special Senses', title: 'Special Senses', icon: eyeIcon },
        { id: 'Psychiatry', title: 'Psychiatry', icon: psychiatryIcon },
        { id: 'Renal', title: 'Renal', icon: kidneyIcon },
        { id: 'Reproductive', title: 'Reproductive', icon: reproductiveIcon },
        { id: 'Respiratory', title: 'Respiratory', icon: lungsIcon },
        { id: 'Rapid Review', title: 'Rapid Review', icon: revisionIcon },
        { id: 'Sketchy Micro', title: 'Sketchy Micro', icon: infectiousIcon }
      ]
    },
    {
      id: 'USMLE Step 2 -',
      title: 'USMLE Step 2',
      icon: stepTwoIcon,
      chapters: [
        { id: 'CARDIOVASCULAR', title: 'CARDIOVASCULAR', icon: heartIcon },
        { id: 'RESPIRATORY_&_ENT', title: 'RESPIRATORY_&_ENT', icon: lungsIcon },
        { id: 'RENAL_&_UROLOGY', title: 'RENAL_&_UROLOGY', icon: kidneyIcon },
        { id: 'ENDOCRINE', title: 'ENDOCRINE', icon: endocrineIcon },
        { id: 'HEMATOLOGY_&_ONCOLOGY', title: 'HEMATOLOGY_&_ONCOLOGY', icon: bloodIcon },
        { id: 'IMMUNOLOGY', title: 'IMMUNOLOGY', icon: immunologyIcon },
        { id: 'GIT', title: 'GIT', icon: gitIcon },
        { id: 'MUSCULOSKELETAL', title: 'MUSCULOSKELETAL', icon: musculoskeletalIcon },
        { id: 'NEUROLOGY', title: 'NEUROLOGY', icon: cnsIcon },
        { id: 'OPHTHALMOLOGY', title: 'OPHTHALMOLOGY', icon: eyeIcon },
        { id: 'PSYCHIATRY', title: 'PSYCHIATRY', icon: psychiatryIcon },
        { id: 'DERMATOLOGY', title: 'DERMATOLOGY', icon: skinIcon },
        { id: 'GYNECOLOGY_&_OBSTETRICS', title: 'GYNECOLOGY_&_OBSTETRICS', icon: reproductiveIcon },
        { id: 'NEONATE_&_INFANT', title: 'NEONATE_&_INFANT', icon: newbornIcon },
        { id: 'GENETICS', title: 'GENETICS', icon: biochemistryIcon },
        { id: 'INFECTIOUS_DISEASE', title: 'INFECTIOUS_DISEASE', icon: infectiousIcon },
        { id: 'SURGERY_&_ANESTHESIA', title: 'SURGERY_&_ANESTHESIA', icon: surgeryIcon },
        { id: 'EMERGENCY,_TRAUMA_&_TOXICOLOGY', title: 'EMERGENCY,_TRAUMA_&_TOXICOLOGY', icon: emergencyIcon },
        { id: 'THE_WELL_PATIENT', title: 'THE_WELL_PATIENT', icon: wellPatientIcon },
        { id: 'BIOSTATISTICS_&_EPIDEMIOLOGY', title: 'BIOSTATISTICS_&_EPIDEMIOLOGY', icon: statisticsIcon },
        { id: 'ETHICS_&_COMMUNICATION', title: 'ETHICS_&_COMMUNICATION', icon: publicHealthIcon },
        { id: 'HEALTHCARE_DELIVERY', title: 'HEALTHCARE_DELIVERY', icon: healthcareIcon },
        { id: 'QUALITY_&_SAFETY', title: 'QUALITY_&_SAFETY', icon: qualityIcon },
        { id: 'PHARMACOLOGY_REVIEW', title: 'PHARMACOLOGY_REVIEW', icon: pharmaIcon }
      ]
    }
  ];

  return (
    <div className={`my_nts_note_container ${isDarkMode ? 'my_nts_dark-mode' : ''}`}>
      {/* Replace SharedHeader with DashboardHeader */}
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />

      {/* Main Content */}
      <div className="my_nts_note_main">
        <div className="my_nts_note_title">
          <h1>My Notes - {userProfile?.firstName || user?.email?.split('@')[0]}</h1>
        </div>

        <div className="my_nts_note_steps_container">
          {steps.map(step => (
            <div key={step.id} className="my_nts_note_step_section">
              <div 
                className="my_nts_note_step_header"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleStep(step.id);
                }}
              >
                <img src={step.icon} alt={step.title} className="my_nts_note_step_icon" />
                <h2>{step.title}</h2>
                <span className={`my_nts_note_collapse_arrow ${expandedSteps[step.id] ? 'expanded' : ''}`}>
                  ▼
                </span>
              </div>
              {expandedSteps[step.id] && (
                <div className="my_nts_note_chapters_grid">
                  {step.chapters.map(chapter => (
                    <div 
                      key={chapter.id} 
                      className="my_nts_note_chapter_card"
                      onClick={(e) => handleChapterClick(e, step.id, chapter.id)}
                    >
                      <div className="my_nts_note_chapter_icon_wrapper">
                        <img src={chapter.icon} alt={chapter.title} />
                      </div>
                      <h3>{step.id.includes('Step 2') ? getDisplayTitle(chapter.title) : chapter.title}</h3>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Update DashboardFooter */}
      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default MyNotes;
